/**
 * OfflineIndicator — shows a subtle banner when the user goes offline.
 *
 * Uses `navigator.onLine` + `online`/`offline` events to detect connectivity.
 * Also monitors Dexie Cloud sync status for more granular feedback.
 *
 * The banner appears at the top of the app when offline, and auto-dismisses
 * when connectivity returns. Never blocks any user actions — the app works
 * offline by design.
 *
 * NEVER-01: Never deletes data. Never blocks functionality.
 */

import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import WifiOff from "@/shared/icons/pikaicons/stroke/devices/wifi-off.svg?react"
import Wifi from "@/shared/icons/pikaicons/stroke/devices/wifi-on.svg?react"
import { cn } from "@/shared/utils/general/cn"

export function OfflineIndicator() {
  const { t } = useTranslation()
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true,
  )
  const [showReconnected, setShowReconnected] = useState(false)
  const [wasOffline, setWasOffline] = useState(false)

  useEffect(() => {
    let offlineTimer: ReturnType<typeof setTimeout> | null = null
    let reconnectedTimer: ReturnType<typeof setTimeout> | null = null

    const handleOnline = () => {
      // Cancel pending offline transition — it was just a blip
      if (offlineTimer) {
        clearTimeout(offlineTimer)
        offlineTimer = null
      }
      setIsOnline(true)
      if (wasOffline) {
        setShowReconnected(true)
        reconnectedTimer = setTimeout(() => setShowReconnected(false), 3000)
      }
    }
    const handleOffline = () => {
      // Debounce: wait 3s before showing offline — ignore quick app suspend/resume blips
      offlineTimer = setTimeout(() => {
        if (!navigator.onLine) {
          setIsOnline(false)
          setWasOffline(true)
        }
      }, 3000)
    }

    // Re-check network state when app regains focus (iOS suspend/resume)
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        if (navigator.onLine) {
          handleOnline()
        }
      }
    }
    const handleFocus = () => {
      if (navigator.onLine) {
        handleOnline()
      }
    }

    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)
    document.addEventListener("visibilitychange", handleVisibility)
    window.addEventListener("focus", handleFocus)

    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
      document.removeEventListener("visibilitychange", handleVisibility)
      window.removeEventListener("focus", handleFocus)
      if (offlineTimer) clearTimeout(offlineTimer)
      if (reconnectedTimer) clearTimeout(reconnectedTimer)
    }
  }, [wasOffline])

  // Don't render anything when online (and no reconnected message)
  if (isOnline && !showReconnected) return null

  return (
    <div
      className={cn(
        "fixed left-0 right-0 z-[9999] flex items-center justify-center px-4 py-1.5 text-xs font-medium transition-all duration-300",
        !isOnline &&
          "bg-amber-500/90 text-amber-950 dark:bg-amber-600/90 dark:text-amber-50",
        showReconnected &&
          isOnline &&
          "bg-emerald-500/90 text-emerald-950 dark:bg-emerald-600/90 dark:text-emerald-50",
      )}
      style={{
        top: "var(--status-bar-height, 0px)",
      }}
    >
      {!isOnline ? (
        <>
          <WifiOff className="size-3 mr-1.5" />
          {t("You're offline — changes will sync when you reconnect")}
        </>
      ) : (
        <>
          <Wifi className="size-3 mr-1.5" />
          {t("Back online")}
        </>
      )}
    </div>
  )
}
